import type { ResponseType } from 'axios';
import qs from 'qs';

import { Trace } from './logging';
import { WeakCache } from './memory-cache';
import { fspApiLimit } from './WindowContext';

import uefaAxios, { paramsSerializer } from '@core/axios';

export const formatUrl = (baseURL: string, url: string, params?: Record<string, any>) =>
  uefaAxios.getUri({ baseURL, url, params: qs.parse(paramsSerializer(params)) });

const fspLimit = fspApiLimit ?? 50;

const callsCache = new WeakCache<string, Promise<any>>();

/**
 * Fetch with calls cache
 * @param baseURL base url
 * @param url path
 * @param params query params
 * @param headers custom headers
 * @param responseType response type
 * @param signal abort controller signal
 * @returns the data
 */
const customFetch = <T = any>(
  baseURL: string,
  url: string,
  params: Record<string, any>,
  headers: Record<string, any>,
  responseType: ResponseType,
  signal: AbortSignal,
) => {
  const key = formatUrl(baseURL, url, params);

  return callsCache.getOrSet(key, async () => {
    try {
      const resp = await uefaAxios.get<T>(url, { params, headers, baseURL, signal, responseType });

      return resp.data;
    } catch (error) {
      if (error.name === 'AbortError' || signal?.aborted) {
        throw error;
      }
      Trace(`Error calling url: ${key}`, error);
      return undefined;
    } finally {
      callsCache.delete(key);
    }
  }) as Promise<T>;
};

/**
 * Load from API with cache
 * @param url - The API url to call
 * @param params - Object of params
 * @param options - Object of options
 * @param options.apiUrl - Default baseUrl
 * @param options.apiKey - API apiKey
 * @param options.hideFromWeb - Hide from web
 * @param options.responseType - Response type
 * @param options.signal - Abort controller signal
 * @param apiUrl - Custom baseUrl
 */
export function loadFromAPIs<T = any>(
  url: string,
  params?: { [key: string]: any },
  options: {
    apiUrl?: string;
    apiKey?: string;
    hideFromWeb?: boolean;
    responseType?: ResponseType;
    signal?: AbortSignal;
  } = {},
) {
  const headers = options.apiKey ? { 'x-api-key': options.apiKey } : {};
  if (options.hideFromWeb) {
    url += url.includes('?') ? '&' : '?';
    url += 'param.attributes.hideFromWeb!=true';
  }

  return customFetch<T>(options.apiUrl, url, params, headers, options.responseType ?? 'json', options.signal);
}

export async function recursiveCall<T = any>(
  url: string,
  params?: { [key: string]: any },
  options: {
    apiUrl?: string;
    apiKey?: string;
    hideFromWeb?: boolean;
    responseType?: 'json' | 'text';
    signal?: AbortSignal;
    dataProperty?: string;
  } = {},
) {
  let result: T[] = [];
  let tempResult: T[] = [];
  let iteration = 0;

  params.limit ??= fspLimit;
  let limit = params.limit > fspLimit ? fspLimit : params.limit;

  do {
    tempResult = await loadFromAPIs<T[]>(
      url,
      {
        ...params,
        limit,
        offset: (params.offset ?? 0) + iteration * fspLimit,
      },
      options,
    );

    tempResult = (options?.dataProperty ? tempResult?.[options.dataProperty] : tempResult) ?? [];

    iteration++;
    result = [...result, ...tempResult];
    if (tempResult.length < fspLimit || result.length >= params.limit) break;

    limit = params.limit - result.length > fspLimit ? fspLimit : params.limit - result.length;
  } while (tempResult.length && iteration < 20);

  if (result.length > params.limit) {
    result.length = params.limit;
  }

  return result;
}
